import { useState } from 'react';
import { Link } from 'react-router';
import type { AlertStatus, SignalAlert, StrategyEvaluation, TrendLabel } from '@contracts/signals';
import { loadPrefs, permission, requestPermission, savePrefs, signalPrice, type Permission } from '../lib/notify';
import { dateTime, relative } from '../lib/format';
import { useLive } from '../store/live';
import { Empty, Panel } from './Panel';

const STATUS_TEXT: Record<AlertStatus, string> = {
  open: 'Open',
  tp2: 'TP2 reached',
  tp3: 'TP3 reached',
  sl: 'Stop loss hit',
  expired: 'Expired',
  reversed: 'Closed by opposite signal',
};

/** One take-profit (the H1 Stochastic alerts): TP1 == TP2 and no TP3. */
export function singleTarget(alert: SignalAlert): boolean {
  return alert.tp3 == null && alert.tp1 === alert.tp2;
}

export function DirectionChip({ direction }: { direction: 'BUY' | 'SELL' | 'WAIT' }) {
  const cls = direction === 'BUY' ? 'dir--buy' : direction === 'SELL' ? 'dir--sell' : 'dir--none';
  const icon = direction === 'BUY' ? '▲' : direction === 'SELL' ? '▼' : '◇';
  return (
    <span className={`dir ${cls}`}>
      <span aria-hidden="true">{icon}</span> {direction}
    </span>
  );
}

export function AlertStatusBadge({ alert }: { alert: SignalAlert }) {
  if (singleTarget(alert)) {
    const text = alert.status === 'tp2' ? 'TP reached' : STATUS_TEXT[alert.status];
    const cls = alert.status === 'tp2' ? 'badge--live' : alert.status === 'sl' ? 'badge--danger' : 'badge--neutral';
    return <span className={`badge ${cls}`}>{text}</span>;
  }
  const best = alert.highestTp ?? (alert.tp1HitAt ? 1 : 0);
  const tone =
    alert.status === 'tp2' || alert.status === 'tp3'
      ? 'badge--live'
      : alert.status === 'sl' && best === 0
        ? 'badge--danger'
        : 'badge--neutral';
  const reached = best > 0 && alert.status !== 'tp2' && alert.status !== 'tp3' ? ` · TP${best} reached` : '';
  const text = `${STATUS_TEXT[alert.status]}${reached}`;
  return <span className={`badge ${tone}`}>{text}</span>;
}

export function AlertLevels({ alert }: { alert: SignalAlert }) {
  if (singleTarget(alert)) {
    const risk = Math.abs(alert.entry - alert.stopLoss);
    const reward = Math.abs(alert.tp1 - alert.entry);
    return (
      <div className="levels levels--4" aria-label="Signal levels">
        <div className="level level--entry">
          <div className="level__label">Entry ({alert.direction === 'BUY' ? 'Ask' : 'Bid'})</div>
          <div className="level__value tabular">{signalPrice(alert.entry)}</div>
        </div>
        <div className="level level--sl">
          <div className="level__label">Stop loss (wick)</div>
          <div className="level__value tabular">{signalPrice(alert.stopLoss)}</div>
        </div>
        <div className="level level--tp">
          <div className="level__label">Take profit</div>
          <div className="level__value tabular">{signalPrice(alert.tp1)}</div>
        </div>
        <div className="level">
          <div className="level__label">At 0.01 lot</div>
          <div className="level__value tabular">
            +${reward.toFixed(2)} / −${risk.toFixed(2)}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`levels ${alert.tp3 != null ? 'levels--5' : 'levels--4'}`} aria-label="Signal levels">
      <div className="level level--entry">
        <div className="level__label">Entry ({alert.direction === 'BUY' ? 'Ask' : 'Bid'})</div>
        <div className="level__value tabular">{signalPrice(alert.entry)}</div>
      </div>
      <div className="level level--sl">
        <div className="level__label">Stop loss</div>
        <div className="level__value tabular">{signalPrice(alert.stopLoss)}</div>
      </div>
      <div className="level level--tp">
        <div className="level__label">TP1</div>
        <div className="level__value tabular">{signalPrice(alert.tp1)}</div>
      </div>
      <div className="level level--tp">
        <div className="level__label">TP2</div>
        <div className="level__value tabular">{signalPrice(alert.tp2)}</div>
      </div>
      {alert.tp3 != null ? (
        <div className="level level--tp">
          <div className="level__label">TP3</div>
          <div className="level__value tabular">{signalPrice(alert.tp3)}</div>
        </div>
      ) : null}
    </div>
  );
}

/** The trend label stored with an alert (label only), if any. */
export function trendOf(details: Record<string, unknown> | undefined): TrendLabel | null {
  const t = details?.['trend'];
  return t && typeof t === 'object' && typeof (t as TrendLabel).text === 'string' ? (t as TrendLabel) : null;
}

/** "เทรนด์ H4: 📉 ขาลงแรง — ⚠️ สัญญาณนี้สวนเทรนด์" — text and icon carry the meaning, colour only adds to it. */
export function TrendNote({ trend }: { trend: TrendLabel | null }) {
  if (!trend) return null;
  const cls = trend.relation === 'counter' ? 'neg' : trend.relation === 'with' ? 'pos' : 'muted';
  return (
    <p className={`small ${cls}`} style={{ margin: 0 }} data-testid="trend-note">
      {trend.text}
    </p>
  );
}

function reasons(alert: SignalAlert): string[] {
  const r = alert.details?.['reasons'];
  return Array.isArray(r) ? r.filter((x): x is string => typeof x === 'string') : [];
}

/** ATR(14) of H1 at the signal bar; alerts created before it was stored have none. */
export function alertAtr(alert: SignalAlert): number | null {
  const v = alert.details?.['atr'];
  return typeof v === 'number' && v > 0 ? v : null;
}

export function AtrBadge({ alert }: { alert: SignalAlert }) {
  const atr = alertAtr(alert);
  return atr === null ? null : <span className="small muted">(ATR H1 ${atr.toFixed(1)})</span>;
}

/** "TP +$5.00 ≈ 0.3 ATR · SL $15.15 ≈ 0.9 ATR" (without the ATR parts when the alert has no ATR). */
function atrText(alert: SignalAlert): string {
  const atr = alertAtr(alert);
  const tp = Math.abs(alert.tp1 - alert.entry);
  const sl = Math.abs(alert.entry - alert.stopLoss);
  const of = (v: number) => (atr ? ` ≈ ${(v / atr).toFixed(1)} ATR` : '');
  return `TP +$${tp.toFixed(2)}${of(tp)} · SL $${sl.toFixed(2)}${of(sl)}`;
}

export function AlertCard({ alert }: { alert: SignalAlert }) {
  return (
    <article className="alert-card" aria-label={`${alert.direction} signal, ${alert.strategyLabel}`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <DirectionChip direction={alert.direction} />
          <strong>{alert.strategyLabel}</strong>
          <AtrBadge alert={alert} />
        </div>
        <AlertStatusBadge alert={alert} />
      </div>
      <AlertLevels alert={alert} />
      {singleTarget(alert) && reasons(alert).length > 0 ? (
        <p className="small" style={{ margin: 0 }}>
          เหตุผล: {reasons(alert).join(' + ')}
        </p>
      ) : null}
      <TrendNote trend={trendOf(alert.details)} />
      <p className="small muted" style={{ margin: 0 }}>
        {dateTime(alert.createdAt)} ·{' '}
        {singleTarget(alert)
          ? atrText(alert)
          : `net R ${alert.netRR1.toFixed(2)} (TP1) / ${alert.netRR2.toFixed(2)} (TP2)`}{' '}
        · spread {signalPrice(alert.spread)}
        {alert.dataSource !== 'live' ? ` · ${alert.dataSource.toUpperCase()} DATA` : ''}
        {alert.newsChecked === false ? ' · ⚠ news not checked' : ''}
      </p>
    </article>
  );
}

export function StrategyState({ evaluation }: { evaluation: StrategyEvaluation }) {
  const passed = evaluation.gates.filter((g) => g.passed).length;
  return (
    <div className="strategy-state">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>{evaluation.strategyLabel}</strong>
        <DirectionChip direction={evaluation.direction} />
      </div>
      <p className="small" style={{ margin: '4px 0' }}>
        {evaluation.summary}
      </p>
      <details>
        <summary className="small">
          {evaluation.candidate === null
            ? 'No setup forming — details'
            : evaluation.strategy.startsWith('halftrend') || evaluation.strategy === 'stoch_h1'
              ? 'Details'
              : `Conditions: ${passed}/${evaluation.gates.length} passed`}
          {evaluation.candidate && evaluation.direction === 'WAIT' ? ` (leaning ${evaluation.candidate})` : ''}
        </summary>
        <ul className="gates">
          {evaluation.gates.map((g) => (
            <li key={g.code} className={`gate small ${g.passed ? 'gate--pass' : 'gate--fail'}`}>
              <span aria-hidden="true">{g.passed ? '✓' : '✗'}</span>{' '}
              <span className="visually-hidden">{g.passed ? 'Passed' : 'Not met'}: </span>
              {g.detail}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

export function AlertControls() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [perm, setPerm] = useState<Permission>(permission);
  const update = (next: typeof prefs) => {
    setPrefs(next);
    savePrefs(next);
  };
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
      {perm === 'granted' ? (
        <span className="badge badge--live">
          <span aria-hidden="true">🔔</span> Browser notifications on
        </span>
      ) : perm === 'unsupported' ? (
        <span className="badge badge--neutral">Browser notifications not supported</span>
      ) : perm === 'denied' ? (
        <span className="badge badge--warn" title="Allow notifications for this site in the browser settings">
          Notifications blocked in the browser
        </span>
      ) : (
        <button type="button" className="btn btn--primary" onClick={() => void requestPermission().then(setPerm)}>
          <span aria-hidden="true">🔔</span> Enable notifications
        </button>
      )}
      <label className="small row" style={{ gap: 4 }}>
        <input
          type="checkbox"
          checked={prefs.enabled}
          onChange={(e) => update({ ...prefs, enabled: e.target.checked })}
        />
        Alerts
      </label>
      <label className="small row" style={{ gap: 4 }}>
        <input
          type="checkbox"
          checked={prefs.sound}
          disabled={!prefs.enabled}
          onChange={(e) => update({ ...prefs, sound: e.target.checked })}
        />
        Sound
      </label>
    </div>
  );
}

/** Dashboard panel: current state of both strategies and the latest alerts. */
export function SignalPanel() {
  const status = useLive((s) => s.signalStatus);
  const alerts = useLive((s) => s.alerts);
  const connection = useLive((s) => s.connections.signals);
  return (
    <Panel
      id="signals"
      title="สัญญาณ Stochastic — H1 80/20 + แท่งเทียนกลับตัว"
      meta={status?.evaluatedAt ? `H1 checked ${relative(status.evaluatedAt)}` : 'Waiting for the next H1 close'}
    >
      <AlertControls />
      {status?.newsFilter && !status.newsFilter.active ? (
        <p className="small" role="note" style={{ margin: '8px 0 0' }}>
          <span aria-hidden="true">⚠</span> <strong>News filter off</strong> — {status.newsFilter.reason}. Check the
          economic calendar yourself before acting on an alert.
        </p>
      ) : null}
      {connection !== 'open' ? <p className="small muted">Signal feed {connection}…</p> : null}
      <div className="grid grid--2" style={{ marginTop: 12 }}>
        {status && status.strategies.length > 0 ? (
          status.strategies.map((e) => <StrategyState key={e.strategy} evaluation={e} />)
        ) : (
          <Empty title="No evaluation yet">The rule is checked after every closed H1 candle.</Empty>
        )}
      </div>
      <h3 className="section-title" style={{ marginTop: 16 }}>
        Latest alerts
      </h3>
      {alerts.length === 0 ? (
        <Empty title="No BUY/SELL alerts yet">WAIT is the normal state — every condition must pass.</Empty>
      ) : (
        <div className="stack">
          {alerts.slice(0, 3).map((a) => (
            <AlertCard key={a.id} alert={a} />
          ))}
          <Link to="/signals" className="small">
            All alerts →
          </Link>
        </div>
      )}
      <p className="disclaimer">
        Alerts only — no order is placed. Signals are rule-based setups, not predictions; these rules have not been
        shown to be profitable. Not financial advice.
      </p>
    </Panel>
  );
}

/** Pop-up for alerts that arrive while the app is open (also when browser notifications are off). */
export function SignalToasts() {
  const fresh = useLive((s) => s.freshAlerts);
  const dismiss = useLive((s) => s.dismissAlert);
  if (fresh.length === 0) return null;
  return (
    <div className="toasts" role="region" aria-label="New signal alerts">
      {fresh.map((a) => (
        <div key={a.id} className={`toast toast--${a.direction.toLowerCase()}`} role="alert">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="row">
              <DirectionChip direction={a.direction} />
              <strong className="small">{a.strategyLabel}</strong>
            </div>
            <button type="button" className="btn" aria-label="Dismiss alert" onClick={() => dismiss(a.id)}>
              ✕
            </button>
          </div>
          <AlertLevels alert={a} />
        </div>
      ))}
    </div>
  );
}
